import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import { type NativeStackNavigationProp } from '@react-navigation/native-stack';
import { type OperationContext } from 'urql';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useOptInUserMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { useNavigateToMainTabs } from '../../../../navigation';
import { type ModalStackParamList } from '../../../../navigation/AppNavigation.props';

export function useSweetpassOptIn(props: UseSweetpassOptInProps) {
  const {
    planId,
    campaignId,
    redirectToAfterUpgrade,
    redirectToOrderId,
    refetchSweetpassTier,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { track } = useTelemetry();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const navigateToMainTabs = useNavigateToMainTabs();
  const navigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  // ─── Mutation ────────────────────────────────────────────────────────

  const [optInMutationResponse, optInMutation] = useOptInUserMutation();
  const isConfirmingOptIn = optInMutationResponse.fetching;

  // ─── Join Sweetpass ──────────────────────────────────────────────────

  const handleJoinSweetpass = useCallback(async () => {
    track('sweetpass_account.opt_in.tapped');

    const { data } = await optInMutation();
    const hasOptedInToFreeTier = Boolean(data?.optInFreeTier);

    if (!hasOptedInToFreeTier) {
      addNoticeBanner({ text: t('general.error'), palette: 'caution' });

      return;
    }

    refetchSweetpassTier({ requestPolicy: 'cache-and-network' });
    void navigateToMainTabs('LoyaltyTab', {
      screen: 'SweetpassHome',
    });
  }, [
    track,
    optInMutation,
    refetchSweetpassTier,
    navigateToMainTabs,
    addNoticeBanner,
    t,
  ]);

  // ─── Proceed To Checkout ─────────────────────────────────────────────

  const handleProceedToCheckout = useCallback(async () => {
    track('sweetpass_account.upgrade_cta.tapped');

    if (planId) {
      navigation.navigate('SweetpassCheckout', {
        planId,
        campaignId,
        redirectToAfterUpgrade,
        redirectToOrderId,
      });
    }
  }, [
    navigation,
    campaignId,
    planId,
    redirectToAfterUpgrade,
    redirectToOrderId,
    track,
  ]);

  // ─── Opt-In Callback ─────────────────────────────────────────────────

  const optInSweetpass = useCallback(
    async (action?: string) => {
      if (action === 'join_sweetpass') return handleJoinSweetpass();

      return handleProceedToCheckout();
    },
    [handleJoinSweetpass, handleProceedToCheckout],
  );

  return { isConfirmingOptIn, optInSweetpass };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassOptInProps = Readonly<{
  planId?: string;
  campaignId?: string;
  redirectToAfterUpgrade?: 'bag' | 'order-status';
  redirectToOrderId?: string;
  refetchSweetpassTier: (context: Partial<OperationContext>) => void;
}>;
